import React, { useEffect, useRef, useState } from 'react';
import { Shield, Bell, Moon, Sun, Laptop, LogOut, Smartphone, UserCheck, ChevronDown, Users } from 'lucide-react';
import { GroupSettings, GroupMemberProfile, UserAccount } from '../types';
import { useTheme } from './ThemeToggle';
import { GigaBallaLogo } from './GigaBallaLogo';

interface NavbarProps {
  user: UserAccount;
  group: GroupSettings;
  members: GroupMemberProfile[];
  activeUserId: string;
  onLogout: () => void;
  onOpenNotifications: () => void;
  unreadCount?: number;
  pendingApprovalCount?: number;
  onNavigate: (tab: string) => void;
  currentTab: string;
  onOpenRegisterPlayer?: () => void;
  onOpenAuthModal?: () => void;
  onOpenApkModal?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  user,
  group,
  members,
  activeUserId,
  onLogout,
  onOpenNotifications,
  unreadCount = 2,
  pendingApprovalCount = 0,
  onNavigate,
  currentTab,
  onOpenRegisterPlayer,
  onOpenAuthModal,
  onOpenApkModal
}) => {
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const { theme, setTheme } = useTheme();
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const activeMember = members.find(m => m.userId === activeUserId) || members[0];

  const isAdmin = ['owner', 'admin'].includes(activeMember?.role || '');

  useEffect(() => {
    if (!showRoleDropdown) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!dropdownRef.current) return;
      const target = event.target as Node | null;
      if (target && !dropdownRef.current.contains(target)) {
        setShowRoleDropdown(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowRoleDropdown(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showRoleDropdown]);

  useEffect(() => {
    setShowRoleDropdown(false);
  }, [currentTab]);

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-amber-500/10 text-amber-400 border-amber-500/30';
      case 'admin':
      case 'organizer': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';
      case 'guest': return 'bg-purple-500/10 text-purple-300 border-purple-500/30';
      default: return 'bg-blue-500/10 text-blue-300 border-blue-500/30';
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-[#080b0a]/95 backdrop-blur-md border-b border-white/5 px-2 sm:px-4 py-2 sm:py-3 w-full max-w-full overflow-visible">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-1.5 sm:gap-2 min-w-0">
        
        {/* Left: Brand Logo & Club Title */}
        <div className="flex items-center gap-1.5 sm:gap-2 cursor-pointer select-none shrink min-w-0" onClick={() => onNavigate('home')}>
          <div className="relative flex items-center justify-center shrink-0">
            <GigaBallaLogo size={32} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1 min-w-0">
              <h1 className="font-black text-xs sm:text-base text-white tracking-tight flex items-center gap-0.5 min-w-0 truncate">
                Giga<span className="text-[#f97316]">Balls</span>
              </h1>
              <span className="hidden sm:inline-block text-[10px] text-orange-400 font-mono font-bold px-1.5 py-0.5 rounded-md bg-orange-500/10 border border-orange-500/20">
                #{group.joinCode || 'GB2026'}
              </span>
            </div>
            <p className="text-[9px] sm:text-[10px] text-slate-400 font-medium flex items-center gap-1">
              <span className="text-[#f97316] font-bold">Turf Club</span>
              <span className="hidden sm:inline">• {group.currency} BDT</span>
            </p>
          </div>
        </div>

        {/* Center Desktop Navigation Tabs */}
        <nav className="hidden lg:flex items-center gap-1 bg-[#121614] p-1 rounded-xl border border-white/5">
          {[
            { id: 'home', label: 'Matchday' },
            { id: 'matches', label: 'Fixtures' },
            { id: 'leaderboard', label: 'Stats' },
            { id: 'media', label: 'Media & Photos' },
            { id: 'turfs', label: 'Turfs' },
            { id: 'payments', label: 'Ledger' },
            { id: 'football', label: 'World' },
            { id: 'admin', label: 'Admin 🔒', adminOnly: true },
          ].map((item) => {
            if (item.adminOnly && !isAdmin) return null;
            const isActive = currentTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => onNavigate(item.id)}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  isActive 
                    ? 'bg-[#1c221d] text-[#a8c936] border border-white/10' 
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* Right Controls: simplified for mobile */}
        <div className="flex items-center gap-1 sm:gap-2 shrink-0 min-w-0">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowRoleDropdown(!showRoleDropdown)}
              className="flex items-center gap-1.5 bg-[#121614] hover:bg-[#171c19] border border-white/5 px-2 sm:px-3 py-1.5 rounded-xl transition text-left touch-manipulation max-w-[118px] sm:max-w-none"
              title="Owner controls"
            >
              <UserCheck className="w-3.5 h-3.5 text-[#a8c936] shrink-0" />
              <div className="min-w-0">
                <span className="block text-[10px] sm:text-xs font-bold text-slate-200 leading-tight truncate">
                  {isAdmin && pendingApprovalCount > 0 ? `Requests ${pendingApprovalCount}` : 'Controls'}
                </span>
                <span className="hidden sm:block text-[10px] text-slate-500 truncate">{activeMember?.displayName}</span>
              </div>
              <ChevronDown className={`w-3 h-3 text-slate-400 shrink-0 transition-transform ${showRoleDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showRoleDropdown && isAdmin && (
              <>
                <button
                  type="button"
                  aria-label="Close owner controls"
                  onClick={() => setShowRoleDropdown(false)}
                  className="fixed inset-0 z-40 bg-black/35 backdrop-blur-[1px]"
                />
                <div className="fixed inset-x-3 top-[72px] z-50 rounded-[28px] bg-[#121614] border border-white/10 shadow-2xl p-3 md:absolute md:inset-x-auto md:top-auto md:right-0 md:mt-2 md:w-[320px] md:max-w-[calc(100vw-24px)]">
                  <div className="text-[11px] sm:text-[12px] font-semibold text-slate-300 px-3 py-2 border-b border-white/5 mb-3">
                    Owner Controls
                  </div>

                  <div className="w-full flex items-center justify-between p-3 sm:p-4 rounded-[24px] text-left bg-[#1a201c] border border-[#313a33] mb-3 gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-slate-800 overflow-hidden border border-white/10 shrink-0">
                        <img src={activeMember?.avatar || user.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100'} alt={activeMember?.displayName || user.displayName} className="w-full h-full object-cover" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-lg sm:text-2xl font-black text-white leading-tight truncate">{activeMember?.displayName || user.displayName}</div>
                        <div className="text-[10px] sm:text-xs text-slate-400 font-mono truncate">ID: {activeUserId} • OVR {activeMember?.footballCard?.overall ?? 0}</div>
                      </div>
                    </div>
                    <span className={`text-[10px] sm:text-xs font-bold uppercase px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-2xl border shrink-0 ${getRoleBadge(activeMember?.role || 'player')}`}>
                      {activeMember?.role || 'member'}
                    </span>
                  </div>

                  {onOpenAuthModal && (
                    <button
                      onClick={() => {
                        setShowRoleDropdown(false);
                        onOpenAuthModal();
                      }}
                      className="w-full flex items-center justify-center gap-3 p-4 sm:p-5 rounded-[24px] bg-[#1b1f1d] hover:bg-[#222826] text-white font-bold text-base sm:text-lg transition mb-3"
                    >
                      <UserCheck className="w-5 h-5 sm:w-6 sm:h-6 text-[#a8c936]" />
                      <span>{pendingApprovalCount > 0 ? `Player Requests (${pendingApprovalCount})` : 'Player Requests'}</span>
                    </button>
                  )}

                  {onOpenRegisterPlayer && (
                    <button
                      onClick={() => {
                        setShowRoleDropdown(false);
                        onOpenRegisterPlayer();
                      }}
                      className="w-full flex items-center justify-center gap-3 p-4 sm:p-5 rounded-[24px] bg-[#283913] hover:bg-[#314619] text-[#b4db45] font-extrabold text-base sm:text-lg transition mb-3"
                    >
                      <Users className="w-5 h-5 sm:w-6 sm:h-6" />
                      <span>Create Player Manually</span>
                    </button>
                  )}

                  <div className="grid grid-cols-2 gap-2 sm:hidden">
                    <button
                      type="button"
                      onClick={() => {
                        setShowRoleDropdown(false);
                        onOpenNotifications();
                      }}
                      className="flex items-center justify-center gap-2 px-3 py-3 rounded-2xl bg-[#171c19] text-slate-200 border border-white/5"
                    >
                      <Bell className="w-4 h-4" />
                      <span className="text-xs font-bold">Alerts</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowRoleDropdown(false);
                        onLogout();
                      }}
                      className="flex items-center justify-center gap-2 px-3 py-3 rounded-2xl bg-red-500/10 text-red-300 border border-red-500/20"
                    >
                      <LogOut className="w-4 h-4" />
                      <span className="text-xs font-bold">Logout</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <button type="button" onClick={onLogout} title="Sign out" className="hidden sm:flex p-2 rounded-xl bg-[#121614] hover:bg-red-500/20 border border-white/5 text-slate-300 hover:text-red-300 transition">
            <LogOut className="w-4 h-4" />
          </button>
          {onOpenApkModal && (
            <button
              type="button"
              onClick={onOpenApkModal}
              title="Download Android APK & Web App Setup"
              className="hidden sm:flex px-2.5 py-1.5 rounded-xl bg-orange-500/15 hover:bg-orange-500/25 border border-orange-500/30 text-orange-400 font-extrabold text-xs transition items-center gap-1.5 touch-manipulation active:scale-95"
            >
              <Smartphone className="w-4 h-4" />
              <span className="hidden sm:inline">APK</span>
            </button>
          )}

          {/* Quick Theme Toggle Icon */}
          <button
            onClick={() => {
              const nextTheme = theme === 'dark' ? 'light' : theme === 'light' ? 'system' : 'dark';
              setTheme(nextTheme);
            }}
            title={`Current theme: ${theme}. Click to switch theme.`}
            className="hidden sm:flex p-2 rounded-xl bg-[#121614] hover:bg-[#171c19] border border-white/5 text-slate-300 hover:text-white transition items-center justify-center"
          >
            {theme === 'dark' && <Moon className="w-4 h-4 text-[#a8c936]" />}
            {theme === 'light' && <Sun className="w-4 h-4 text-amber-400" />}
            {theme === 'system' && <Laptop className="w-4 h-4 text-slate-400" />}
          </button>

          {/* Notifications Bell */}
          <button
            onClick={onOpenNotifications}
            className="hidden sm:flex relative p-2 rounded-xl bg-[#121614] hover:bg-[#171c19] border border-white/5 text-slate-300 hover:text-white transition items-center justify-center"
          >
            <Bell className="w-4 h-4 text-slate-300" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#a8c936] text-[#080b0a] font-bold text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>

          {/* User Profile Avatar Link */}
          <button
            onClick={() => onNavigate('profile')}
            className={`relative rounded-xl overflow-hidden border transition ${
              currentTab === 'profile' ? 'border-[#a8c936]' : 'border-white/10 hover:border-white/20'
            }`}
          >
            <img src={activeMember?.avatar || user.avatar} alt={user.displayName} className="w-8 h-8 object-cover" />
          </button>

        </div>
      </div>
    </header>
  );
};
